import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fmt } from '../data/metrics';
import { SEV_META } from '../data/insights';
import type {
  ConnectedSourceId,
  GuestReviewInsightCard,
  GuestReviewOutletBucket,
  GuestReviewOutletRow,
  GuestReviewsPayload,
  GuestReviewTimelineRow,
  InsightSeverity,
} from '../types';
import { Icon } from './shared/Icon';
import { relDelta, trendStats } from './shared/Trend';
import { DrillMetricTabs, REVIEWS_TAB_KEY, type DrillTabDef } from './shared/DrillMetricTabs';
import { DrillToolbar, DrillToolbarStat } from './shared/DrillToolbar';

const MIN_DRIVER_REVIEWS = 3;

const INSIGHT_TONE: Record<
  GuestReviewInsightCard['tone'],
  InsightSeverity
> = {
  risk: 'alert',
  win: 'opportunity',
  watch: 'info',
};

function StoryCard({ card }: { card: GuestReviewInsightCard }) {
  const sev = INSIGHT_TONE[card.tone];
  const meta = SEV_META[sev];
  return (
    <div
      className={`drill-insight drill-insight--${sev} gr-insight-card`}
      style={{ borderLeftColor: meta.c }}
    >
      <span className="di-badge" style={{ background: meta.c }}>
        <Icon name={card.icon} size={15} fill={sev !== 'info'} />
      </span>
      <div className="di-main">
        <div className="di-kicker" style={{ color: meta.c }}>
          {meta.label}
        </div>
        <div className="di-title">
          {card.title}
          <span className="gr-insight-value">{card.value}</span>
        </div>
        <div className="di-why">{card.detail}</div>
        <div className="di-action">
          <Icon
            name="check"
            size={14}
            stroke={2.3}
            style={{ color: meta.c, marginTop: 1 }}
          />
          <span>{card.action}</span>
        </div>
      </div>
    </div>
  );
}

function ratingTone(rating: number): string {
  if (rating <= 0) return 'var(--ink-3)';
  if (rating >= 4.3) return 'var(--st-steady)';
  if (rating >= 3.8) return 'var(--ink)';
  return 'var(--st-alert)';
}

function ReviewTimeline({ rows }: { rows: GuestReviewTimelineRow[] }) {
  const { t } = useTranslation('performance');
  if (!rows.length) {
    return <p className="si-empty">{t('reviews.noActivity')}</p>;
  }

  const maxReviews = Math.max(...rows.map(r => r.reviewCount), 1);

  return (
    <div className="gr-timeline">
      <div className="gr-timeline-head">
        <span>{t('reviews.period')}</span>
        <span className="gr-th-bar">{t('reviews.reviews')}</span>
        <span className="gr-th-num">{t('reviews.avgRating')}</span>
        <span className="gr-th-num">{t('reviews.guests')}</span>
      </div>
      {rows.map(row => {
        const barPct =
          row.reviewCount > 0
            ? Math.max(6, (row.reviewCount / maxReviews) * 100)
            : 0;
        return (
          <div className="gr-timeline-row" key={row.bucketKey}>
            <div className="gr-timeline-label" title={row.bucketKey}>
              {row.label}
            </div>
            <div className="gr-vol-cell">
              <span className="gr-vol-count">{fmt.num(row.reviewCount)}</span>
              <span className="gr-vol-track" aria-hidden>
                <span
                  className="gr-vol-fill"
                  style={{ width: `${barPct}%` }}
                />
              </span>
            </div>
            <div
              className="gr-num gr-rating"
              style={{ color: ratingTone(row.avgRating) }}
            >
              {row.avgRating > 0 ? `${row.avgRating.toFixed(1)}★` : '-'}
            </div>
            <div className="gr-num gr-num-guests">{fmt.num(row.covers)}</div>
          </div>
        );
      })}
    </div>
  );
}

const MAX_OUTLET_HISTORY = 24;

// Per-outlet review history: one bar per aggregation period, height by review
// volume and tinted by that period's average rating, so each bar carries both
// the number and the average. Mirrors the reservation-source mini-trend style.
function OutletReviewBars({ series }: { series?: GuestReviewOutletBucket[] }) {
  const { t } = useTranslation('performance');
  if (!series || series.length < 2) {
    return <p className="gr-obars-empty">{t('reviews.notEnoughHistory')}</p>;
  }
  const max = Math.max(...series.map(s => s.reviewCount), 1);
  return (
    <div className="gr-obars">
      {series.map(b => {
        const h = b.reviewCount > 0 ? Math.max(6, (b.reviewCount / max) * 100) : 0;
        const ratio =
          b.avgRating > 0
            ? Math.min(1, Math.max(0, (b.avgRating - 3.5) / 1.5))
            : 0;
        const color =
          b.avgRating > 0
            ? `color-mix(in srgb, var(--st-ok, var(--st-steady)) ${Math.round(
                ratio * 100,
              )}%, var(--st-alert))`
            : 'var(--line-2)';
        return (
          <div
            className="gr-obar-col"
            key={b.bucketKey}
            title={`${b.label}: ${t('reviews.reviewCount', {
              count: b.reviewCount,
              value: fmt.num(b.reviewCount),
            })} · ${
              b.avgRating > 0
                ? t('reviews.avgStar', { rating: b.avgRating.toFixed(1) })
                : t('reviews.noRatings')
            }`}
          >
            <span className="gr-obar-count">
              {b.reviewCount > 0 ? fmt.num(b.reviewCount) : '-'}
            </span>
            <span className="gr-obar-track">
              <span
                className="gr-obar-fill"
                style={{ height: `${h}%`, background: color }}
              />
            </span>
            <span className="gr-obar-rating">
              {b.avgRating > 0 ? `${b.avgRating.toFixed(1)}★` : ''}
            </span>
            <span className="gr-obar-lab">{b.label}</span>
          </div>
        );
      })}
    </div>
  );
}

function OutletReputation({
  rows,
  fleetAvg,
}: {
  rows: GuestReviewOutletRow[];
  fleetAvg: number;
}) {
  const { t } = useTranslation('performance');
  const [visible, setVisible] = useState(MAX_OUTLET_HISTORY);
  const shown = rows.slice(0, visible);
  const hidden = rows.length - shown.length;
  return (
    <div className="drill-panel">
      <div className="dp-head">
        <h3>{t('reviews.outletReputation')}</h3>
        <span>{t('reviews.outletsMeta', { count: rows.length })}</span>
      </div>
      <div className="gr-outlets">
        {shown.map(row => {
          const vsTone =
            row.vsFleetAvg > 0.05
              ? 'var(--st-steady)'
              : row.vsFleetAvg < -0.05
                ? 'var(--st-alert)'
                : 'var(--ink-3)';
          return (
            <div className="gr-outlet" key={row.restaurantId}>
              <div className="gr-outlet-head">
                <div className="gr-outlet-name">
                  <b>{row.name}</b>
                  <span>
                    {t('reviews.reviewCount', {
                      count: row.reviewCount,
                      value: fmt.num(row.reviewCount),
                    })}
                    {' · '}
                    {t('reviews.outletStats', {
                      replied: row.responseRatePct,
                      negShare: row.negativeSharePct,
                      unanswered: row.unansweredNegative,
                    })}
                  </span>
                </div>
                <div className="gr-outlet-rating">
                  <span className="gr-outlet-rv">
                    {row.avgRating.toFixed(1)}★
                  </span>
                  <span className="gr-outlet-vs" style={{ color: vsTone }}>
                    {t('reviews.vsFleet', {
                      delta: `${row.vsFleetAvg >= 0 ? '+' : ''}${row.vsFleetAvg.toFixed(1)}`,
                      avg: fleetAvg.toFixed(1),
                    })}
                  </span>
                </div>
              </div>
              <OutletReviewBars series={row.series} />
            </div>
          );
        })}
      </div>
      {hidden > 0 && (
        <div className="gr-outlets-more">
          <span className="gr-axis-note">
            {t('reviews.showingTop', { shown: shown.length, hidden })}
          </span>
          <button
            type="button"
            className="gr-load-more"
            onClick={() =>
              setVisible(v => Math.min(rows.length, v + MAX_OUTLET_HISTORY))
            }
          >
            <Icon name="chevron" size={13} style={{ transform: 'rotate(90deg)' }} />
            {t('reviews.loadMore', {
              count: Math.min(hidden, MAX_OUTLET_HISTORY),
            })}
          </button>
          {rows.length > MAX_OUTLET_HISTORY && (
            <button
              type="button"
              className="gr-load-more"
              onClick={() => setVisible(rows.length)}
            >
              {t('reviews.showAll', { count: rows.length })}
            </button>
          )}
        </div>
      )}
      {hidden === 0 && rows.length > MAX_OUTLET_HISTORY && (
        <div className="gr-outlets-more">
          <button
            type="button"
            className="gr-load-more"
            onClick={() => setVisible(MAX_OUTLET_HISTORY)}
          >
            <Icon name="chevron" size={13} style={{ transform: 'rotate(-90deg)' }} />
            {t('situation.collapse')}
          </button>
        </div>
      )}
    </div>
  );
}

// Pearson correlation coefficient. Returns null when the sample is too small or
// either series has no variance (a flat line can't correlate with anything).
function pearson(xs: number[], ys: number[]): number | null {
  const n = Math.min(xs.length, ys.length);
  if (n < 3) return null;
  let sx = 0;
  let sy = 0;
  for (let i = 0; i < n; i += 1) {
    sx += xs[i];
    sy += ys[i];
  }
  const mx = sx / n;
  const my = sy / n;
  let num = 0;
  let dx = 0;
  let dy = 0;
  for (let i = 0; i < n; i += 1) {
    const a = xs[i] - mx;
    const b = ys[i] - my;
    num += a * b;
    dx += a * a;
    dy += b * b;
  }
  if (dx === 0 || dy === 0) return null;
  return num / Math.sqrt(dx * dy);
}

// labelKey / subKey / readingKey are performance-namespace i18n keys,
// resolved with t() when the card renders.
type CorrelationResult = {
  key: string;
  labelKey: string;
  subKey: string;
  icon: string;
  r: number | null;
  sample: number;
  readingKey: string;
  tone: 'good' | 'bad' | 'neutral' | 'none';
};

function corrSignalTone(
  tone: CorrelationResult['tone'],
): 'win' | 'risk' | 'watch' {
  if (tone === 'good') return 'win';
  if (tone === 'bad') return 'risk';
  return 'watch';
}

function linkHeadlineKey(r: number | null): string {
  if (r == null) return 'reviews.link.notEnoughData';
  const absR = Math.abs(r);
  if (absR < 0.2) return 'reviews.link.noClearPattern';
  if (absR >= 0.8) return 'reviews.link.veryStrong';
  if (absR >= 0.66) return 'reviews.link.strong';
  if (absR >= 0.33) return 'reviews.link.moderate';
  return 'reviews.link.slight';
}

function linkDirectionKey(r: number | null): string | null {
  if (r == null) return null;
  if (Math.abs(r) < 0.2) return null;
  return r > 0 ? 'reviews.link.riseTogether' : 'reviews.link.moveOpposite';
}

function CorrelationRow({
  result,
  kicker,
  primary = false,
}: {
  result: CorrelationResult;
  kicker?: string;
  primary?: boolean;
}) {
  const { t } = useTranslation('performance');
  const signalTone = corrSignalTone(result.tone);
  const directionKey = linkDirectionKey(result.r);
  const directionIcon =
    result.r != null && Math.abs(result.r) >= 0.2
      ? result.r > 0
        ? 'trendUp'
        : 'arrowDown'
      : 'link';

  return (
    <div className="gr-corr-item">
      {kicker ? (
        <div className={`ph-group-lab ${signalTone}`}>
          <Icon name="target" size={13} />
          {kicker}
        </div>
      ) : null}
      <div className={`ph-signal soft ${signalTone}${primary ? ' primary' : ''}`}>
        <div className="ph-signal-head">
          <span className="ph-signal-ic">
            <Icon name={result.icon} size={14} />
          </span>
          <span>{t(result.labelKey)}</span>
        </div>
        <div className="ph-signal-value">{t(linkHeadlineKey(result.r))}</div>
        {directionKey ? (
          <div className="gr-corr-direction">
            <Icon name={directionIcon} size={13} />
            <span>{t(directionKey)}</span>
          </div>
        ) : null}
        <div className="ph-signal-detail">{t(result.subKey)}</div>
        <div className="ph-signal-action">{t(result.readingKey)}</div>
      </div>
    </div>
  );
}

function classify(
  r: number | null,
  sample: number,
  favorableWhenPositive: boolean,
  posKey: string,
  negKey: string,
): { readingKey: string; tone: CorrelationResult['tone'] } {
  if (r == null) {
    return {
      readingKey:
        sample < 3
          ? 'reviews.reading.notEnoughWeeks'
          : 'reviews.reading.noMoveTogether',
      tone: 'none',
    };
  }
  const absR = Math.abs(r);
  if (absR < 0.2) {
    return {
      readingKey: 'reviews.reading.noConnection',
      tone: 'neutral',
    };
  }
  const favorable = r > 0 === favorableWhenPositive;
  return {
    readingKey: r > 0 ? posKey : negKey,
    tone: favorable ? 'good' : 'bad',
  };
}

// Copy for each pair lives under performance:reviews.series.<key>.{label,sub,pos,neg}.
const SERIES_DEFS: Array<{
  key: string;
  icon: string;
  x: keyof GuestReviewTimelineRow;
  y: keyof GuestReviewTimelineRow;
  ratingBased: boolean;
  favorableWhenPositive: boolean;
}> = [
  {
    key: 'coversRating',
    icon: 'users',
    x: 'covers',
    y: 'avgRating',
    ratingBased: true,
    favorableWhenPositive: true,
  },
  {
    key: 'volumeRating',
    icon: 'star',
    x: 'reviewCount',
    y: 'avgRating',
    ratingBased: true,
    favorableWhenPositive: true,
  },
  {
    key: 'responseRating',
    icon: 'check',
    x: 'responseRatePct',
    y: 'avgRating',
    ratingBased: true,
    favorableWhenPositive: true,
  },
  {
    key: 'coversVolume',
    icon: 'activity',
    x: 'covers',
    y: 'reviewCount',
    ratingBased: false,
    favorableWhenPositive: true,
  },
];

// Copy for each driver lives under performance:reviews.drivers.<key>.{label,sub,pos,neg}.
const DRIVER_DEFS: Array<{
  key: string;
  icon: string;
  field: keyof GuestReviewOutletRow;
  favorableWhenPositive: boolean;
}> = [
  // Deliberately excludes "1–2★ share": it is definitionally part of the
  // average rating, so correlating the two is a tautology, not an insight.
  // These predictors are independent of how the rating is computed.
  {
    key: 'response',
    icon: 'check',
    field: 'responseRatePct',
    favorableWhenPositive: true,
  },
  {
    key: 'volume',
    icon: 'star',
    field: 'reviewCount',
    favorableWhenPositive: true,
  },
];

const MIN_DRIVER_OUTLETS = 3;

function DriversAndCorrelations({ data }: { data: GuestReviewsPayload }) {
  const { t } = useTranslation('performance');
  const seriesResults = useMemo<CorrelationResult[]>(() => {
    const tl = data.timeline;
    return SERIES_DEFS.map(def => {
      const rows = def.ratingBased
        ? tl.filter(row => row.reviewCount > 0 && row.avgRating > 0)
        : tl;
      const xs = rows.map(row => Number(row[def.x]));
      const ys = rows.map(row => Number(row[def.y]));
      const r = pearson(xs, ys);
      const base = `reviews.series.${def.key}`;
      const { readingKey, tone } = classify(
        r,
        rows.length,
        def.favorableWhenPositive,
        `${base}.pos`,
        `${base}.neg`,
      );
      return {
        key: def.key,
        labelKey: `${base}.label`,
        subKey: `${base}.sub`,
        icon: def.icon,
        r,
        sample: rows.length,
        readingKey,
        tone,
      };
    });
  }, [data.timeline]);

  const driverResults = useMemo<CorrelationResult[]>(() => {
    const rows = (data.outletRows ?? []).filter(
      o => o.reviewCount >= MIN_DRIVER_REVIEWS,
    );
    const ratings = rows.map(o => o.avgRating);
    return DRIVER_DEFS.map(def => {
      const xs = rows.map(o => Number(o[def.field]));
      const r = pearson(xs, ratings);
      const base = `reviews.drivers.${def.key}`;
      const { readingKey, tone } = classify(
        r,
        rows.length,
        def.favorableWhenPositive,
        `${base}.pos`,
        `${base}.neg`,
      );
      return {
        key: def.key,
        labelKey: `${base}.label`,
        subKey: `${base}.sub`,
        icon: def.icon,
        r,
        sample: rows.length,
        readingKey,
        tone,
      };
    }).sort((a, b) => Math.abs(b.r ?? 0) - Math.abs(a.r ?? 0));
  }, [data.outletRows]);

  const driverSample = (data.outletRows ?? []).filter(
    o => o.reviewCount >= MIN_DRIVER_REVIEWS,
  ).length;
  const topDriver = driverResults.find(
    d => d.r != null && Math.abs(d.r) >= 0.2,
  );

  return (
    <div className="drill-panel">
      <div className="dp-head">
        <h3>{t('reviews.whatMoves')}</h3>
        <span>{t('reviews.patternsPeriod')}</span>
      </div>

      <div className="gr-drill">
        <div className="gr-drill-block">
          <div className="dg-chart-label">{t('reviews.metricsOverTime')}</div>
          <p className="gr-drill-hint">
            {t('reviews.comparedWeeks', { count: data.timeline.length })}
          </p>
          <div className="ph-cards">
            {seriesResults.map(result => (
              <CorrelationRow key={result.key} result={result} />
            ))}
          </div>
        </div>

        <div className="gr-drill-block">
          <div className="dg-chart-label">{t('reviews.separates')}</div>
          {driverSample < MIN_DRIVER_OUTLETS ? (
            <p className="si-empty">
              {t('reviews.needOutlets', {
                needed: MIN_DRIVER_OUTLETS,
                min: MIN_DRIVER_REVIEWS,
                current: driverSample,
              })}
            </p>
          ) : (
            <>
              <div className="ph-cards">
                {topDriver ? (
                  <CorrelationRow
                    result={topDriver}
                    kicker={t('reviews.biggestDifference')}
                    primary
                  />
                ) : null}
                {driverResults
                  .filter(result => result.key !== topDriver?.key)
                  .map(result => (
                    <CorrelationRow key={result.key} result={result} />
                  ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function GuestReviewsDrillDown({
  data,
  connected,
  onClose,
  onSwitchMetric,
}: {
  data: GuestReviewsPayload;
  scopeLabel?: string;
  connected?: ConnectedSourceId[];
  onClose: () => void;
  onSwitchMetric?: (tab: DrillTabDef) => void;
}) {
  const { t } = useTranslation('performance');
  const fleet = data.fleet;
  const outletRows = data.outletRows ?? [];

  // Period-over-period rating move, from buckets that actually carry reviews.
  const ratedBuckets = data.timeline.filter(
    t => t.reviewCount > 0 && t.avgRating > 0,
  );
  const ratingSeries = ratedBuckets.map(t => t.avgRating);
  const ratingStats = trendStats(ratingSeries);
  const prevLabel =
    ratedBuckets.length > 1
      ? ratedBuckets[ratedBuckets.length - 2].label
      : t('drawer.priorPeriod');
  const ratingDelta =
    data.comparable && ratingStats.hasSeries
      ? relDelta(ratingStats.latest, ratingStats.prev)
      : null;
  const deltaGood = ratingDelta != null && ratingDelta >= 0;

  return (
    <div className="drill drill-below-topbar" data-ftue="reviews-drill">
      <DrillToolbar
        onClose={onClose}
        periodLabel={data.periodLabel}
        hero={
          fleet ? (
            <DrillToolbarStat
              label={t('metrics:sentiment.short')}
              value={`${fleet.avgRating.toFixed(1)}★`}
              trend={
                ratingDelta != null ? (
                  <div
                    className="drill-hero-d drill-toolbar-trend"
                    style={{
                      color: deltaGood ? 'var(--st-steady)' : 'var(--st-alert)',
                    }}
                  >
                    <Icon
                      name={ratingDelta >= 0 ? 'arrowUp' : 'arrowDown'}
                      size={13}
                      stroke={2.4}
                    />
                    {Math.abs(ratingDelta)}%{' '}
                    {t('drillUi.vsLabel', { label: prevLabel })}
                  </div>
                ) : undefined
              }
            />
          ) : undefined
        }
      >
        {onSwitchMetric && (
          <DrillMetricTabs
            active={REVIEWS_TAB_KEY}
            reviewsAvailable
            connected={connected}
            onSelect={tab => {
              if (tab.key !== REVIEWS_TAB_KEY) onSwitchMetric(tab);
            }}
          />
        )}
      </DrillToolbar>

      <div className="drill-body">
        {!fleet ? (
          <p className="si-empty">{t('reviews.noMapped')}</p>
        ) : (
          <>
            {data.insights.length > 0 && (
              <div className="drill-extra">
                {data.insights.map(card => (
                  <StoryCard
                    card={card}
                    key={`${card.title}-${card.value}`}
                  />
                ))}
              </div>
            )}

            <div className="drill-panel">
              <div className="dp-head">
                <h3>{t('reviews.fleetReputation')}</h3>
                <span>{data.periodLabel}</span>
              </div>
              <div className="dg-kpi-row">
                <div className="dg-kpi">
                  <span className="dg-kpi-label">{t('reviews.averageRating')}</span>
                  <strong>{fleet.avgRating.toFixed(1)}★</strong>
                  <span className="dg-kpi-sub">
                    {t('reviews.reviewsInPeriod', {
                      count: fleet.totalReviews,
                      value: fmt.num(fleet.totalReviews),
                    })}
                  </span>
                </div>
                <div className="dg-kpi">
                  <span className="dg-kpi-label">{t('reviews.responseRate')}</span>
                  <strong>{fleet.responseRatePct}%</strong>
                  <span className="dg-kpi-sub">
                    {t('reviews.unansweredNegative', {
                      value: fleet.unansweredNegativeCount,
                    })}
                  </span>
                </div>
                <div className="dg-kpi">
                  <span className="dg-kpi-label">{t('reviews.reviewsPer100')}</span>
                  <strong>{fleet.reviewsPer100Covers}</strong>
                  <span className="dg-kpi-sub">{t('reviews.intensity')}</span>
                </div>
                <div className="dg-kpi">
                  <span className="dg-kpi-label">{t('reviews.platforms')}</span>
                  <strong>
                    {fleet.byPlatform.map(p => p.platform).join(' · ') || '-'}
                  </strong>
                  <span className="dg-kpi-sub">
                    {fleet.byPlatform
                      .map(p => `${p.platform}: ${p.avgRating.toFixed(1)}★`)
                      .join(' · ')}
                  </span>
                </div>
              </div>

              <div className="dg-chart-label">{t('reviews.storyOverTime')}</div>
              <ReviewTimeline rows={data.timeline} />
            </div>

            {outletRows.length > 0 && (
              <OutletReputation rows={outletRows} fleetAvg={fleet.avgRating} />
            )}

            <DriversAndCorrelations data={data} />
          </>
        )}
      </div>
    </div>
  );
}
